import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ProfileTemplateService', 'templates');

export interface ProfileTemplateFeatures {
  hasContactForm?: boolean;
  hasSocialLinks?: boolean;
  hasSpecialties?: boolean;
}

export interface ProfileTemplate {
  id: string;
  name: string;
  description: string | null;
  preview_image_url: string | null;
  features: ProfileTemplateFeatures;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export class ProfileTemplateService {
  /**
   * Get all active templates for gallery display
   */
  static async getActiveTemplates(): Promise<ProfileTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('profile_templates')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) {
        log.error('Failed to fetch active templates', {}, error);
        throw error;
      }

      log.info('Fetched active templates', { count: data?.length || 0 });
      return data || [];
    } catch (error) {
      log.error('Error in getActiveTemplates', {}, error as Error);
      throw error;
    }
  }

  /**
   * Get template by ID
   */
  static async getTemplateById(id: string): Promise<ProfileTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('profile_templates')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error && error.code !== 'PGRST116') { // Not found is ok
        log.error('Failed to fetch template by ID', { id }, error);
        throw error;
      }

      return data;
    } catch (error) {
      log.error('Error in getTemplateById', { id }, error as Error);
      throw error;
    }
  }

  /**
   * Generate preview URL dynamically
   */
  static getPreviewUrl(templateId: string): string {
    return `/templates/preview/${templateId}`;
  }

  /**
   * Generate demo URL dynamically
   */
  static getDemoUrl(templateId: string): string {
    return `/templates/demo/${templateId}`;
  }

  /**
   * Check if a template exists and is active
   */
  static async isTemplateAvailable(templateId: string): Promise<boolean> {
    try {
      const template = await this.getTemplateById(templateId);
      return template?.is_active === true;
    } catch {
      return false;
    }
  }

  /**
   * Get template display name
   */
  static getTemplateDisplayName(templateId: string): string {
    const displayNames: Record<string, string> = {
      none: 'No Website',
      'professional-1': 'Professional Profile',
      'minimal-1': 'Modern Minimal',
      'tech-1': 'Tech Forward',
      custom: 'Use My Own Website'
    };
    return displayNames[templateId] || templateId;
  }
}
